// Copyright (c) 2016 Mattermost, Inc. All Rights Reserved.
// See License.txt for license information.

import { bind } from 'bind-decorator';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

class AutosizeTextarea extends React.Component {
  static defaultProps = {
    errorText: '',
    onHeightChange: () => null
  };

  static scrollIntoViewOptions = {
    behavior: 'auto',
    block: 'start',
    inline: 'nearest'
  };

  textarea = React.createRef();
  reference = React.createRef();

  constructor(props) {
    super(props);

    this.height = 0;
  }

  @bind
  recalculateSize() {
    const { current: textarea } = this.textarea;
    const { current: reference } = this.reference;

    const height = reference.scrollHeight;

    if (height > 0 && height !== this.height && textarea) {
      const style = getComputedStyle(textarea);
      const borderWidth =
        parseInt(style.borderTopWidth, 10) + parseInt(style.borderBottomWidth, 10);

      // Directly change the height to avoid circular rerenders
      textarea.style.height = String(height + borderWidth) + 'px';

      this.height = height;

      this.props.onHeightChange(height, parseInt(style.maxHeight, 10));
    }
  }

  @bind
  getDOMNode() {
    return this.textarea.current;
  }

  @bind
  focus() {
    const { current: textarea } = this.textarea;
    if (!!textarea) {
      textarea.focus();
    }
    this.recalculateSize();
  }

  @bind
  scrollIntoView(override) {
    const { current: textarea } = this.textarea;
    if (!textarea) {
      return;
    }

    const options = override != null ? override : AutosizeTextarea.scrollIntoViewOptions;
    textarea.scrollIntoView(options);
  }

  componentDidMount() {
    this.recalculateSize();
  }

  componentDidUpdate() {
    this.recalculateSize();
  }

  render() {
    const props = { ...this.props };

    Reflect.deleteProperty(props, 'onHeightChange');
    Reflect.deleteProperty(props, 'providers');
    Reflect.deleteProperty(props, 'channelId');

    const { errored, errorText, placeholder, value, ...otherProps } = props;

    const heightProps = {};
    if (this.height <= 0) {
      // Set an initial number of rows so that the textarea doesn't appear too large when its first rendered
      heightProps.rows = 1;
    } else {
      heightProps.height = this.height;
    }
    return (
      <div style={{ width: '100%' }}>
        <textarea
          ref={this.textarea}
          {...heightProps}
          {...props}
          className={cx({
            [this.props.className]: true,
            error: this.props.errored,
            warning: this.props.warning
          })}
        />
        {!!errored && (
          <div className="error-message">
            <small>{errorText}</small>
          </div>
        )}
        <div style={{ height: 0, width: '100%', overflow: 'hidden' }}>
          <textarea
            ref={this.reference}
            style={{ height: 'auto', width: '100%' }}
            value={value || placeholder}
            {...otherProps}
            disabled={true} /* Prevent tabbing into this field */
          />
        </div>
      </div>
    );
  }
}

AutosizeTextarea.propTypes = {
  value: PropTypes.string,
  placeholder: PropTypes.string,
  onHeightChange: PropTypes.func
};

export default AutosizeTextarea;
